<template>
    <div class="border-t border-gray-200">
        <div class="grid items-center bg-white" :style="gridStyle">
            <button
                type="button"
                class="flex items-center gap-2 px-4 py-3 text-left text-muted transition hover:text-body"
                :aria-expanded="expanded"
                @click="expanded = !expanded"
            >
                <span class="inline-block text-xs transition-transform" :class="expanded ? 'rotate-90' : ''" aria-hidden="true">&rsaquo;</span>
                <span class="flex flex-col gap-0.5">
                    <span class="text-sm font-semibold text-body">{{ row.assetTypeLabel }}</span>
                    <span class="text-xs text-muted">{{ rowMetaLabel }}</span>
                    <span v-if="row.slaStart && row.slaEnd" class="text-xs text-muted">
                        SLA: {{ formatDate(row.slaStart) }} – {{ formatDate(row.slaEnd) }}
                    </span>
                    <span v-if="row.contractingAuthorityName" class="text-xs text-muted">{{ row.contractingAuthorityName }}</span>
                    <span v-if="row.contactName || row.contactEmail" class="text-xs text-muted">
                        <template v-if="row.contactName">{{ row.contactName }}<span v-if="row.contactEmail"> · </span></template>
                        <a v-if="row.contactEmail" :href="`mailto:${row.contactEmail}`" class="text-primary underline">{{ row.contactEmail }}</a>
                    </span>
                </span>
            </button>
            <div
                v-for="(year, index) in years"
                :key="year"
                class="flex items-center justify-around py-2"
                :class="[index > 0 ? 'border-l border-gray-200' : '', showLabels ? 'gap-1 px-2' : 'gap-0.5 px-1']"
            >
                <TimelineCell v-for="cell in cellsForYear(year)" :key="cell.periodStart" :cell="cell" :show-label="showLabels" />
            </div>
        </div>
        <div v-if="expanded">
            <AssetRow
                v-for="asset in row.assets"
                :key="asset.assetId"
                :asset="asset"
                :canonical-cells="row.cells"
                :years="years"
                :show-labels="showLabels"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import fullDate from '@/filters/fullDate';
import { trans } from '@/mixins/i18n';
import TimelineCell from './TimelineCell.vue';
import AssetRow from './AssetRow.vue';
import { ROW_HORIZONTAL_MARGIN_PX, rowGridTemplate, shouldShowCellLabels } from './timelineLayout';

const props = defineProps({
    row: {
        type: Object,
        required: true,
    },
    years: {
        type: Array,
        required: true,
    },
});

const expanded = ref(false);

const cellsByYear = computed(() => {
    const map = new Map();
    for (const cell of props.row.cells ?? []) {
        if (!map.has(cell.year)) {
            map.set(cell.year, []);
        }
        map.get(cell.year).push(cell);
    }
    return map;
});

const cellsForYear = (year) => cellsByYear.value.get(year) ?? [];

// Drop per-cell month labels once a single year packs in more cells than its
// column can label without the text colliding (e.g. monthly cadence). The
// status dots stay; the month remains available on hover. Derived from the
// densest year so the aggregate row and its asset rows agree (same cadence).
const showLabels = computed(() => {
    let maxCellsPerYear = 0;
    for (const cells of cellsByYear.value.values()) {
        if (cells.length > maxCellsPerYear) {
            maxCellsPerYear = cells.length;
        }
    }
    return shouldShowCellLabels(maxCellsPerYear);
});

const assetCountLabel = computed(() => trans('dashboard.v2.preventative_maintenance.timeline.asset_count', { '%d': props.row.assetCount }));
const rowMetaLabel = computed(() =>
    [assetCountLabel.value, props.row.modelSummaryLabel, props.row.cadenceSummaryLabel ?? props.row.cadenceLabel].filter(Boolean).join(' · '),
);

const page = usePage();
const formatDate = (iso) => fullDate(iso, page.props.locale ?? 'en');

const gridStyle = computed(() => ({
    gridTemplateColumns: rowGridTemplate(props.years.length),
    marginLeft: `${ROW_HORIZONTAL_MARGIN_PX}px`,
    marginRight: `${ROW_HORIZONTAL_MARGIN_PX}px`,
}));
</script>
